import React, { useState, useEffect } from "react";
import { get } from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import StripeVerifyService from "../backend/StripeVerifyService";
import Property from "../model/property";
import { withTranslation, useTranslation } from "react-i18next";
import { Modal } from "react-responsive-modal";
import IDSVG from "../assets/precheckin/id.svg";
import { isEmpty } from "lodash";
import ForeignerPassportSVG from "../assets/precheckin/foreigner_passport_icon.svg";
import StripeEnvirontment from "../assets/precheckin/stripe_environtment.png";
import StripeInternet from "../assets/precheckin/stripe_internet.png";
import StripePassport from "../assets/precheckin/stripe_passport.png";
import StripeProfile from "../assets/precheckin/stripe_profile.png";

const StripeVerifyButton = (props) => {
  const {
    propertyUid,
    bookingNumber,
    className,
    styles,
    actionText,
    onClickCallback,
    onCompleteCallback,
    onDeclineCallback,
  } = props;
  const { t } = useTranslation();
  const [userNationality, setUserNationality] = useState(props.userNationality);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(false);
  const [stripe, setStripe] = useState();
  const [skipped, setSkipped] = useState(props.skipped || false);
  const [publishableKey, setPublishableKey] = useState();
  const [clientSecret, setClientSecret] = useState();
  const [token, setToken] = useState(props.token);
  const errorMessage =
    "Sorry! It hasn't configured verification identity yet. Please contact to admin.";
  const stripePromise = async (publishableKey) => {
    try {
      const stripeResponse = await loadStripe(publishableKey, {
        locale: props.language,
      });
      setStripe(stripeResponse);
      return stripeResponse;
    } catch (error) {
      setStripe(null);
      setLoading(false);
      setError(true);
      setSkipped(true);
      props.raiseError(errorMessage);
    }
  };

  const generateSession = async () => {
    props.onLoading(true);
    setLoading(true);
    setModalOpen(false);
    try {
      const { data } = await StripeVerifyService.generateSession(
        token,
        propertyUid,
        bookingNumber,
        userNationality
      );
      setClientSecret(data.id);
      if (typeof onClickCallback === "function") {
        onClickCallback(data.id);
      }
      await verifyIdentity(data.client_secret, data.id);
      setLoading(false);
    } catch (error) {
      setError(true);
      setLoading(false);
      props.raiseError(errorMessage);
    }
  };

  const verifyIdentity = async (sessionId, clientSecret) => {
    let stripeResponse = stripe;
    if (isEmpty(stripeResponse))
      stripeResponse = await stripePromise(publishableKey);
    const { error } = await stripeResponse.verifyIdentity(sessionId);

    if (error) {
      if (typeof onDeclineCallback === "function") {
        onDeclineCallback(clientSecret);
      }
    } else {
      if (typeof onCompleteCallback === "function") {
        onCompleteCallback(clientSecret);
      }
    }
  };

  const handleClick = () => {
    if (skipped) {
      props.onCompleteCallback("");
      return;
    }

    if (props.verified) {
      props.onCompleteCallback(clientSecret);
      return;
    }

    setModalOpen(true);
  };

  const handleStripClick = () => {
    if (skipped) {
      props.onCompleteCallback("");
      return;
    }

    if (error) {
      props.raiseError(errorMessage);
      return;
    }
    if (props.verified) {
      if (typeof onCompleteCallback === "function") {
        onCompleteCallback(clientSecret);
      }
      return;
    }

    // Call your backend to create the VerificationSession.
    generateSession();
  };

  const modalClassNames = {
    closeButton: "close-button-modal",
    modal: "stripe-modal",
    overlay: "overlay-modal",
  };

  const handleUsernationality = (data) => {
    setUserNationality(data);
    props.updateUserNationality(data);
  };

  useEffect(() => {
    const fetchPublishableKey = async () => {
      setLoading(true);
      try {
        const { data } = await StripeVerifyService.getPublishableKey(
          token,
          propertyUid
        );
        if (data.publishable_key !== "") {
          setPublishableKey(data.publishable_key);
          Property.setPublishableKey(propertyUid, data.publishable_key);
          stripePromise(data.publishable_key);
        } else {
          setSkipped(true);
          Property.setPublishableKey(propertyUid, null);
          props.raiseErrorOnPublishableKey(errorMessage);
        }
      } catch (error) {
        if (get(error.response, "status") === 401) {
          const resToken = await props.setAccessTokenData();
          setToken(resToken);
        }
        console.error(error);
        setSkipped(true);
        props.raiseErrorOnPublishableKey(errorMessage);
      }
    };

    if (props.clientSecret && !clientSecret) {
      setClientSecret(props.clientSecret);
    }
    const publishableKeyResponse = Property.getPublishableKey(propertyUid);
    setPublishableKey(publishableKeyResponse);

    if (token) {
      if (
        !error &&
        !loading &&
        !publishableKeyResponse &&
        !skipped &&
        isEmpty(stripe)
      ) {
        fetchPublishableKey();
        setLoading(false);
      }
    }
    // eslint-disable-next-line
  }, [
    token,
    propertyUid,
    stripe,
    loading,
    publishableKey,
    clientSecret,
    skipped,
  ]);

  const closeModal = () => {
    setModalOpen(false);
    const html = document.querySelector("html");
    html.style = "";
  };

  return (
    <>
      <button
        className={className}
        style={styles}
        role="link"
        disabled={loading}
        onClick={() => handleClick()}>
        {actionText}
      </button>
      <Modal
        open={modalOpen}
        onClose={() => closeModal()}
        center
        blockScroll={true}
        classNames={modalClassNames}>
        <div className="relative h-full">
          <div className="pt-6 flex flex-col h-full">
            <div className="px-8 pb-6 md:max-w-2xl md:mx-auto">
              <h2 className="text-center type-normal xLarge mb-6">
                {t("READY_IDENTITY_CHECK")}
              </h2>
              <p className="text-center text-gray-400 type-regular normal">
                {t("HAVE_THESE_READY_AND_ENROLL")}
              </p>
            </div>
            <hr className="md:mb-8 mb-3" />
            <div className="px-8 md:pt-6 pb-2 flex-1 md:max-w-2xl md:mx-auto">
              <div className="flex flex-col space-y-10">
                <div className="p-0 m-0 flex space-x-6 items-center">
                  <div
                    className="w-18 h-18 rounded-full p-4"
                    style={{ backgroundColor: "#FFFAE8" }}>
                    <img
                      src={StripeInternet}
                      alt={t("STABLE_INTERNET_CONNECTION")}
                      className="w-100 h-100"
                    />
                  </div>
                  <p className="type-normal medium leading-3 mb-2">
                    {t("STABLE_INTERNET_CONNECTION")}
                  </p>
                </div>
                <div className="p-0 m-0 flex space-x-6 items-center">
                  <div
                    className="w-18 h-18 rounded-full p-4"
                    style={{ backgroundColor: "#FFFAE8" }}>
                    <img
                      src={StripeProfile}
                      alt={t("OVER_1_8_YO")}
                    />
                  </div>
                  <div>
                    <p className="type-normal medium leading-3 mb-2">
                      {t("OVER_1_8_YO")}
                    </p>
                    <p className="text-gray-400 type-regular normal leading-tight">
                      {t("AT_LEAST_1_8")}
                    </p>
                  </div>
                </div>
                <div className="p-0 m-0 flex space-x-6 items-center">
                  <div
                    className="w-18 h-18 rounded-full p-4"
                    style={{ backgroundColor: "#FFFAE8" }}>
                    <img
                      src={StripePassport}
                      alt={t("PASSPORT_THAT_VALID")}
                    />
                  </div>
                  <div>
                    <p className="type-normal medium leading-3 mb-2">
                      {t("VALID_TRAVEL_DOCUMENT")}
                    </p>
                    <p className="text-gray-400 type-regular normal leading-tight">
                      {t("PASSPORT_THAT_VALID")}
                    </p>
                  </div>
                </div>
                <div className="p-0 m-0 flex space-x-6 items-center">
                  <div
                    className="w-18 h-18 rounded-full p-4"
                    style={{ backgroundColor: "#FFFAE8" }}>
                    <img
                      src={StripeEnvirontment}
                      alt={t("WELL_ENVIRONMENT")}
                    />
                  </div>
                  <p className="type-normal medium leading-3 mb-2">
                    {t("WELL_ENVIRONMENT")}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-8 md:max-w-2xl md:mx-auto h-64 mb-2">
              <div className="flex justify-center">
                <div className="flex flex-col rounded-xl w-64 mb-4 items-center">
                  <label
                    htmlFor="local_nationality"
                    className="flex flex-col justify-center rounded-xl h-52 w-52 p-14 mb-2 mr-4 relative"
                    style={{
                      border: "1px #DBDBDB solid",
                      backgroundColor: "#FAFAFA",
                    }}>
                    <input
                      type="radio"
                      className="hidden"
                      id="local_nationality"
                      value="local"
                      name="userNationality"
                      checked={userNationality === "local"}
                      onChange={() => handleUsernationality("local")}
                    />
                    <div className="absolute top-2 right-2">
                      {userNationality === "local" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8 bg-white rounded-full"
                          viewBox="0 0 512 512"
                          fill="#32C33D"
                          stroke="#fff">
                          <path
                            fill="#32C33D"
                            d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                        </svg>
                      ) : (
                        <div
                          className="w-8 h-8 border rounded-full"
                          style={{ border: "1px #DBDBDB solid" }}></div>
                      )}
                    </div>
                    <img
                      className="text-center"
                      src={IDSVG}
                      alt={props.isHotelKey ? t("U_S_PHOTO_I_D") : t("NATIONAL")}
                    />
                  </label>
                  <p className="capitalize text-center">{props.isHotelKey ? t("U_S_PHOTO_I_D") : t("NATIONAL")}</p>
                </div>
                <div className="flex flex-col rounded-xl w-64 mb-4 items-center">
                  <label
                    htmlFor="foreigner"
                    className="flex flex-col justify-center rounded-xl h-52 w-52 p-14 mb-2 relative"
                    style={{
                      border: "1px #DBDBDB solid",
                      backgroundColor: "#FAFAFA",
                    }}>
                    <input
                      type="radio"
                      className="hidden"
                      id="foreigner"
                      value="foreigner"
                      name="userNationality"
                      checked={userNationality === "foreigner"}
                      onChange={() => handleUsernationality("foreigner")}
                    />
                    <div className="absolute top-2 right-2">
                      {userNationality === "foreigner" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-8 w-8 bg-white rounded-full"
                          viewBox="0 0 512 512"
                          fill="#32C33D"
                          stroke="#fff">
                          <path
                            fill="#32C33D"
                            d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                        </svg>
                      ) : (
                        <div
                          className="w-8 h-8 border rounded-full"
                          style={{ border: "1px #DBDBDB solid" }}></div>
                      )}
                    </div>
                    <img
                      className="text-center"
                      src={ForeignerPassportSVG}
                      alt={props.isHotelKey ? t("PASSPORT") : t("FOREIGNER")}
                    />
                  </label>
                  <p className="capitalize text-center">
                    {props.isHotelKey ? t("PASSPORT") : t("FOREIGNER")}
                  </p>
                </div>
              </div>
            </div>
            <button
              className={`app-button type-title medium tablet-only-style-button ${props.buttonStyleName}`}
              disabled={isEmpty(userNationality)}
              style={{
                backgroundColor: !isEmpty(userNationality)
                  ? props.primaryColor
                  : "#B3B5B5",
                color: props.appHomeTextCallToAction || "#ffffff",
              }}
              onClick={(e) => handleStripClick(e)}>
              {t("I_AM_READY")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default withTranslation()(StripeVerifyButton);
